import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { invalidarFinanceiro, type CentroCusto } from './tipos';

/** M4.2: centros de custo. */
export function CentrosCusto() {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const [aberto, setAberto] = useState<CentroCusto | 'novo' | null>(null);
  const [remover, setRemover] = useState<CentroCusto | null>(null);
  const [importar, setImportar] = useState(false);
  const atual = aberto && aberto !== 'novo' ? aberto : null;

  const excluir = useMutation({
    mutationFn: (c: CentroCusto) => del(`/financeiro/centros-custo/${c.id}`),
    onSuccess: async () => {
      await invalidarFinanceiro(qc);
      setRemover(null);
      setAberto(null);
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
    { nome: 'codigo', rotulo: t('campos.codigo'), tipo: 'texto', largura: 'meia' },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano' },
  ];
  const colunas: Coluna<CentroCusto>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), principal: true, render: (c) => <span className="font-medium">{c.nome}</span> },
    { chave: 'codigo', rotulo: t('campos.codigo'), mono: true, render: (c) => c.codigo ?? '' },
    { chave: 'lancamentos', rotulo: t('campos.lancamentos'), alinhar: 'direita', mono: true },
    { chave: 'ativo', rotulo: t('campos.ativo'), render: (c) => (c.ativo ? <Selo cor="verde">{t('estado.ativo')}</Selo> : <Selo>{t('estado.inativo')}</Selo>) },
  ];

  return (
    <>
      {s.pode('financeiro.criar') && (
        <AcaoPrimaria>
          {s.pode('financeiro.excluir') && <BotaoLixeira entidades={['financeiro.centro_custo']} />}
          {/* Val5 (29/10): importar pede `financeiro.importar`. */}
          {s.pode('financeiro.importar') && s.pode('financeiro.criar') && s.pode('financeiro.editar') && (
            <>
              <BotaoModeloCsv caminho="/financeiro/centros-custo" />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('acoes.importarCentros')}
              </Botao>
            </>
          )}
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoCentro')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<CentroCusto> tela="financeiro.centros" caminho="/financeiro/centros-custo" colunas={colunas} aoClicar={(c) => setAberto(c)} vazio={<Vazio texto={t('vazio.centros')} />} />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? atual.nome : t('acoes.novoCentro')}
        rodape={
          <>
            {atual && s.pode('financeiro.excluir') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemover(atual)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            <>
              <Botao variante="fantasma" onClick={() => setAberto(null)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" type="submit" form="form-centro">
                {tn('comum.salvar')}
              </Botao>
            </>
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-centro"
          campos={campos}
          inicial={atual ? { ...atual } : { ativo: true }}
          aoEnviar={async (v) => {
            const corpo = { nome: v.nome, codigo: v.codigo || null, ativo: !!v.ativo };
            if (atual) await patch(`/financeiro/centros-custo/${atual.id}`, corpo);
            else await post('/financeiro/centros-custo', corpo);
            await invalidarFinanceiro(qc);
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Drawer>
      <Confirmar
        aberto={!!remover}
        aoFechar={() => setRemover(null)}
        aoConfirmar={() => remover && excluir.mutate(remover)}
        carregando={excluir.isPending}
        perigoso
        titulo={t('centros.removerTitulo')}
        texto={t('centros.removerTexto')}
      />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho="/financeiro/centros-custo" titulo={t('acoes.importarCentros')} aoConcluir={() => void invalidarFinanceiro(qc)} />
    </>
  );
}
